<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, ref, watch } from 'vue';

import DividerComponent from '@/components/ui/DividerComponent.vue';
import {
  formatCurrency,
  formatFullDate,
  formatIban,
  formatItemValue,
  formatWithdrawalStatus
} from '@/utils/formatters.ts';
import { WithdrawalStatusKey } from '@/enums/bank_enum.ts';
import { displayModal } from "@/utils/modalUtils.ts";
import BankService from "@/services/bank/bankService.ts";
import { displayToast } from "@/utils/toastUtils.ts";
import OperationStatusComponent from "@/components/bank/OperationStatusComponent.vue";

import type {
  WithdrawalOperationDetailResponse,
  WithdrawalRefuseRequest
} from "@/types/services/bank/bank.type.ts";

const props = defineProps<{
  withdrawalId: number
  drawerMode?: boolean
}>()

const emit = defineEmits<{
  (emit: 'status-updated', payload: {
    withdrawalId: number
    status: WithdrawalStatusKey
  }): void
}>()

const { t } = useI18n()

const loading = ref<boolean>(false);
const withdrawal = ref<WithdrawalOperationDetailResponse | null>(null);

const isPending = computed(() => {
  return withdrawal.value?.status === WithdrawalStatusKey.PENDING;
})

// --- Refuse withdrawal ---
const canRefuse = computed(() => {
  return isPending.value;
});

async function fetchWithdrawal(withdrawalId: number): Promise<void> {
  loading.value = true
  try {
    withdrawal.value = await BankService.getWithdrawal(withdrawalId)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.withdrawalId,
  (id) => {
    withdrawal.value = null
    fetchWithdrawal(id)
  },
  { immediate: true }
)

async function refuseWithdrawal() {
  if (!withdrawal.value) return

  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailWithdrawal.modal.refuse.title'),
      message: t('views.detailWithdrawal.modal.refuse.message'),
      isRefusal: true,
    },
  });

  if (modal) {
    try {
      loading.value = true;
      const { reason } = modal as WithdrawalRefuseRequest;

      await BankService.updateWithdrawalStatus(
        withdrawal.value.withdrawalId,
        WithdrawalStatusKey.REFUSED,
        reason
      );

      displayToast(t('views.detailWithdrawal.refuseSuccess'), 'success');
      withdrawal.value.status = WithdrawalStatusKey.REFUSED;
      emitNewStatus();
    } finally {
      loading.value = false;
    }
  }
}

// --- Validate withdrawal ---
const canValidate = computed(() => {
  return isPending.value;
});

async function validateWithdrawal() {
  if (!withdrawal.value) return

  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailWithdrawal.modal.validate.title'),
      message: t('views.detailWithdrawal.modal.validate.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateWithdrawalStatus(withdrawal.value.withdrawalId, WithdrawalStatusKey.VALIDATED);

      displayToast(t('views.detailWithdrawal.validatedSuccess'), 'success');
      withdrawal.value.status = WithdrawalStatusKey.VALIDATED;
      emitNewStatus();
    } finally {
      loading.value = false;
    }
  }
}

function emitNewStatus() {
  if (!withdrawal.value) return

  emit('status-updated', {
    withdrawalId: withdrawal.value.withdrawalId,
    status: withdrawal.value.status
  })
}
</script>

<template>
  <template v-if="withdrawal">
    <v-row align="center" class="details">
      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.label.mandate') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.mandateName) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.label.status') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-chip :color="formatWithdrawalStatus(withdrawal.status)" class="text-value">
          {{ t('views.detailWithdrawal.status.' + withdrawal.status) }}
        </v-chip>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center" class="user-details">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhUser"/>
        {{ t('views.detailWithdrawal.subtitle.user') }}
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.label.name') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value">
          {{ formatItemValue(withdrawal.username) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.label.email') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value">
          {{ formatItemValue(withdrawal.email) }}
        </p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhWallet"/>
        {{ t('views.detailWithdrawal.subtitle.amount') }}
      </v-col>

      <v-col cols="12">
        <v-card elevation="0" class="text-center text-primary font-weight-bold">
          {{ formatCurrency(withdrawal.amount) }}
        </v-card>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center" class="bank-details">
      <v-col cols="12" class="d-flex align-center">
        <v-icon class="mr-2" color="primary" icon="PhCreditCard"/>
        {{ t("views.detailWithdrawal.bankDetail") }}
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.bankName') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.bankName) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.ownerName') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.ownerName) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.currency') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.currency) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.bic') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.bic) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.iban') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatIban(withdrawal.iban) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailWithdrawal.userRib') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(withdrawal.userRib) }}
        </p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhCalendar"/>
        {{ t('views.detailWithdrawal.subtitle.date') }}
      </v-col>

      <v-col cols="12">
        <p class="text-label">
          {{ formatFullDate(withdrawal.createdAt) }}
        </p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center" justify="space-between" class="mt-4">
      <v-col cols="6">
        <!-- Refuse button -->
        <v-btn
          block
          v-if="canRefuse"
          :loading="loading"
          :disabled="loading"
          color="error"
          type="submit"
          variant="outlined"
          @click="refuseWithdrawal"
        >
          {{ t("views.detailWithdrawal.buttons.refuse") }}
          <template #prepend>
            <v-icon icon="PhX" />
          </template>
        </v-btn>
      </v-col>

      <v-col cols="6">
        <!-- Validate button -->
        <v-btn
          block
          v-if="canValidate"
          :loading="loading"
          :disabled="loading"
          type="submit"
          @click="validateWithdrawal"
        >
          {{ t("views.detailWithdrawal.buttons.validate") }}
          <template #prepend>
            <v-icon icon="PhCheck" />
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </template>
</template>

<style scoped>
.v-card {
  background-color: #fdfaf3;
}

.text-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.text-value {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.87);
}

.details .v-col {
  padding-top: 6px;
  padding-bottom: 6px;
}

.user-details .v-col:not(:first-child) {
  padding-top: 6px;
  padding-bottom: 6px;
}

.bank-details .v-col:not(:first-child) {
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
